import { useCallback, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import CompassIcon from "../assets/CompassIconPlain.png";
import { clearWelcomeBack, readWelcomeBack } from "../auth/welcomeBack";
import { AuthContext } from "../state/AuthContext";

/**
 * SweetAlert-style notice after a successful sign-in. The compass mark is the
 * CompassCare icon. Closing it forgets the flag so a refresh does not show it
 * again. A signed-out session never sees it, including after idle expiry.
 */
export default function WelcomeBackAlert() {
  const location = useLocation();
  const auth = useContext(AuthContext);
  const isAuthenticated = Boolean(auth?.isAuthenticated);
  const isLoading = Boolean(auth?.isLoading);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      clearWelcomeBack();
      setOpen(false);
      return;
    }
    // Sign-in still sits on /login for a moment before the next page loads.
    // Hold the flag for that page; do not open over the form.
    if (location.pathname === "/login") {
      setOpen(false);
      return;
    }
    setOpen(readWelcomeBack());
  }, [isAuthenticated, isLoading, location.key, location.pathname]);

  const close = useCallback(() => {
    clearWelcomeBack();
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    const overlay = document.querySelector("[data-testid=welcome-back-overlay]");
    const parent = overlay?.parentElement;
    const hidden = [];
    if (parent) {
      for (const child of parent.children) {
        if (child === overlay || child.hasAttribute("inert")) continue;
        child.setAttribute("inert", "");
        hidden.push(child);
      }
    }
    const onKey = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close();
        return;
      }
      if (event.key === "Tab") {
        event.preventDefault();
        document.getElementById("welcome-back-ok")?.focus();
      }
    };
    document.addEventListener("keydown", onKey, true);
    document.getElementById("welcome-back-ok")?.focus();
    return () => {
      document.removeEventListener("keydown", onKey, true);
      for (const child of hidden) child.removeAttribute("inert");
      if (previous instanceof HTMLElement) previous.focus();
    };
  }, [open, close]);

  if (!open) return null;

  return (
    <div
      role="presentation"
      data-testid="welcome-back-overlay"
      onClick={close}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 5000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(10, 13, 19, 0.55)",
        padding: 16,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="welcome-back-title"
        data-testid="welcome-back-alert"
        onClick={(event) => event.stopPropagation()}
        style={{
          width: "min(360px, 100%)",
          background: "#fff",
          color: "#1A2433",
          borderRadius: 16,
          padding: "28px 24px 22px",
          textAlign: "center",
          boxShadow: "0 12px 40px rgba(0, 0, 0, 0.28)",
          fontFamily: "IBM Plex Sans, sans-serif",
        }}
      >
        <img
          src={CompassIcon}
          alt=""
          width={96}
          height={96}
          data-testid="welcome-back-logo"
          style={{ borderRadius: "50%", display: "block", margin: "0 auto" }}
        />
        <h2 id="welcome-back-title" style={{ margin: "16px 0 0", fontSize: 26, fontWeight: 600 }}>
          Welcome back
        </h2>
        <button
          id="welcome-back-ok"
          type="button"
          onClick={close}
          style={{
            marginTop: 20,
            background: "#1475BB",
            color: "#fff",
            border: 0,
            borderRadius: 8,
            padding: "10px 28px",
            fontSize: 16,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          OK
        </button>
      </div>
    </div>
  );
}
